import React, { useEffect, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import { DatesSetArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Grid, Stack, Typography, Box } from '@mui/material';
import dayjs from 'dayjs';
import { Helmet } from 'react-helmet';

import { InterviewBlock, UserEvent, EventsByMonth } from '@features/events';
import ApplicantEventInfoModal from '@features/events/components/ApplicantEventInfoModal/ApplicantCalendarEventInfoModal';
import ApplicantUpcomingEvents from '@features/events/components/ApplicantUpcomingEvents/ApplicantUpcomingEvents';
import { useGetApplicantEvents } from '@features/events/hooks/useGetApplicantEvents';
import { primaryBlue, white } from '@styles/colors';
import { Application } from '@features/applications';

export const ApplicantCalender = () => {
  const [selectedEvent, setSelectedEvent] = useState<
    InterviewBlock | UserEvent | null
  >(null);
  const [events, setEvents] = useState<EventsByMonth | []>([]);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [prevMonth, setPrevMonth] = useState(dayjs().subtract(1, 'month'));
  const [nextMonth, setNextMonth] = useState(dayjs().add(1, 'month'));
  const [selectedApplication, setSelectedApplication] =
    useState<Application | null>(null);
  const [selectedBlock, setSelectedBlock] = React.useState(null);
  const [dateInfo, setDateInfo] = useState<{ month: string; year: string }>({
    month: dayjs().format('MM'),
    year: dayjs().format('YYYY'),
  });
  useGetApplicantEvents({
    setEvents,
    month: dateInfo.month,
    year: dateInfo.year
  });

  const handleDateChange = (rangeInfo: DatesSetArg) => {
    const middleDate = dayjs(rangeInfo.start).add(2, 'week');
    setCurrentMonth(middleDate);
    setDateInfo({
      month: middleDate.format('MM'),
      year: middleDate.format('YYYY'),
    });
  };

  useEffect(() => {
    setPrevMonth(currentMonth.subtract(1, 'month'));
    setNextMonth(currentMonth.add(1, 'month'));
  }, [currentMonth]);

  return (
    <>
      <Helmet>
        <title>Calendar | StaffedUp</title>
      </Helmet>
      <Stack component="section" spacing={5} pt={5} pb={4} px={3} pl={6} mb={1}>
        <Box p={3} borderRadius="8px" bgcolor={white}>
          <Typography variant="h3" fontSize="28px" fontWeight="bold">
            Calendar
          </Typography>
        </Box>
      </Stack>
      <Stack component="section" spacing={1} pl={3} pr={3}>
        <Grid container>
          <Grid
            xs={12}
            md={9}
            item
            p={3}
            pt={0}
            sx={{
              '.fc': {
                bgcolor: white,
                p: 3,
                borderRadius: '8px',
              },
            }}
          >
            <style>
              {`
                @media (max-width: 768px) {
                .fc-toolbar {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                }
                .fc-toolbar-chunk {
                    width: 100%;
                    text-align: center;
                }
                }
            `}
            </style>
            <FullCalendar
              plugins={[dayGridPlugin, timeGridPlugin]}
              initialView="dayGridMonth"
              events={
                events && events.filter((item) => (item.event.application.withdrawn === false) && (item.event.application.last_action === 'INTERVIEW PROCESS')).map(
                  (item: {
                    event: {
                      block_end_date: string | number | dayjs.Dayjs | Date | null | undefined;
                      company: any;
                      block_start_date: string | number | dayjs.Dayjs | Date | null | undefined;
                      dt_end_time: | string | number | Date | dayjs.Dayjs | null | undefined;
                      dt_start_time: | string | number | Date | dayjs.Dayjs | null | undefined;
                      slot: {
                        slot_start_time: | string | number | dayjs.Dayjs | Date | null | undefined;
                        slot_end_time: | string | number | dayjs.Dayjs | Date | null | undefined;
                      };
                      block: {
                        dt_start_time: | string | number | dayjs.Dayjs | Date | null | undefined;
                        dt_end_time: | string | number | dayjs.Dayjs | Date | null | undefined;
                      };
                    };
                  }) => {
                    return {
                      start: dayjs(item?.event.dt_start_time).toDate(),
                      end: dayjs(item?.event.dt_end_time).toDate(),
                      title: `${item.event.company.s_name} - Interview`,
                      event: item.event,
                    };
                  },
                )
              }
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              eventBackgroundColor={primaryBlue}
              eventClick={({ event: { extendedProps } }) => {
                setSelectedEvent(extendedProps.event);
                setSelectedBlock(extendedProps.event.id);
              }}
              datesSet={handleDateChange}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack spacing={3} alignItems="center">
              <ApplicantUpcomingEvents
                dateInfo={dateInfo}
                events={events && events.filter((item) => (item.event.application.withdrawn === false) && (item.event.application.last_action === 'INTERVIEW PROCESS'))}
                setSelectedEvent={setSelectedEvent}
                setSelectedBlock={setSelectedBlock}
                setSelectedApplication={setSelectedApplication}
                selectedApplication={selectedApplication}
              />
            </Stack>
          </Grid>
        </Grid>
      </Stack>
      {selectedBlock && (
        <ApplicantEventInfoModal
          setSelectedApplication={setSelectedApplication}
          selectedApplication={selectedApplication}
          event={selectedEvent}
          blockId={selectedBlock}
          setEvents={setEvents}
          handleClose={() => {
            setSelectedBlock(null);
            setSelectedEvent(null);
          }}
        />
      )}
    </>
  );
};
